import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const api = axios.create({
  baseURL: API_BASE + '/api',
  timeout: 15000,
});

// 自动注入 Token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 统一错误处理
api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    const isLoginRequest = err.config?.url?.includes('/auth/login');
    if (err.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err.response?.data || { message: '网络错误' });
  }
);

// --- Auth ---
export const auth = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/password', data),
};

// --- Departments ---
export const depts = {
  list: () => api.get('/depts'),
  detail: (id) => api.get(`/depts/${id}`),
  create: (data) => api.post('/depts', data),
  update: (id, data) => api.put(`/depts/${id}`, data),
  delete: (id) => api.delete(`/depts/${id}`),
};

// --- Employees ---
export const employees = {
  list: () => api.get('/users'),
  detail: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/accounts', data),
  updateDept: (id, data) => api.put(`/users/${id}/department`, data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
};

// --- Performance ---
export const performance = {
  list: (params) => api.get('/performance', { params }),
  create: (data) => api.post('/performance', data),
  batchCreate: (records) => api.post('/performance/batch', { records }),
  importExcel: (formData) => api.post('/performance/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  template: async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get((process.env.REACT_APP_API_BASE || '') + '/api/performance/template', {
      responseType: 'blob',
      headers: token ? { Authorization: 'Bearer ' + token } : {}
    });
    return res.data;
  },
  getTargets: (params) => api.get('/performance/targets', { params }),
  deleteTarget: (userId, params) => api.delete(`/performance/targets/${userId}`, { params }),
  getBusinessTargets: (params) => api.get('/performance/business-targets', { params }),
  setTarget: (data) => api.post('/performance/targets', data),
  batchSetBizTargets: (data) => api.post('/performance/business-targets/batch', data),
  details: (id) => api.get(`/performance/${id}/details`),
  update: (id, data) => api.put(`/performance/${id}`, data),
  delete: (id) => api.delete(`/performance/${id}`),
  recalculate: () => api.post('/performance/recalculate'),
};

// --- Dashboard ---
export const dashboard = {
  summary: (params) => api.get('/dashboard/summary', { params }),
};

// --- Leaderboard ---
export const leaderboard = {
  list: (params) => api.get('/leaderboard', { params }),
  export: async (params) => {
    const token = localStorage.getItem('token');
    const res = await axios.get((process.env.REACT_APP_API_BASE || '') + '/api/leaderboard/export', {
      responseType: 'blob',
      params,
      headers: token ? { Authorization: 'Bearer ' + token } : {}
    });
    return res.data;
  },
};

// --- Service Types (积分规则) ---
export const serviceTypes = {
  list: (params) => api.get('/service-types', { params }),
  departments: () => api.get('/service-types/departments'),
};

// --- Score Details (积分录入) ---
export const scoreDetails = {
  create: (data) => api.post('/score-details', data),
  get: (id) => api.get(`/score-details/${id}`),
};

// --- Alerts ---
export const alerts = {
  list: (params) => api.get('/alerts', { params }),
  markRead: (id) => api.put(`/alerts/${id}/read`),
  markAllRead: () => api.put('/alerts/read-all'),
};

// --- Accounts (账号管理) ---
export const accounts = {
  list: () => api.get('/accounts'),
  managers: () => api.get('/accounts/managers'),
  deleted: () => api.get('/accounts/deleted'),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  restore: (id) => api.put(`/accounts/${id}/restore`),
  resetPassword: (id, password) => api.put(`/accounts/${id}/password`, { password }),
  toggleStatus: (id, is_active) => api.put(`/accounts/${id}/status`, { is_active }),
  delete: (id) => api.delete(`/accounts/${id}`),
};

// --- Business Types (业务类型) ---
export const businessTypes = {
  list: () => api.get('/business-types'),
  create: (data) => api.post('/business-types', data),
  update: (id, data) => api.put(`/business-types/${id}`, data),
  delete: (id) => api.delete(`/business-types/${id}`),
  getByDept: (deptId) => api.get(`/business-types/dept/${deptId}`),
  setByDept: (deptId, data) => api.put(`/business-types/dept/${deptId}`, { business_types: data }),
  deptBindings: () => api.get('/business-types/dept-bindings'),
};

export default api;
